import { setToken } from "./token.js";
import { getShopifySessionToken } from "../lib/appBridge.js";

// Embedded auth path: ask App Bridge for a fresh Shopify session token, hand
// it to our backend in exchange for our admin JWT, and park that JWT in the
// in-memory store. Called on app boot (when embedded) and again after any
// 401 from apiFetch.

interface SessionResponse {
  data: { token: string; expiresIn: number; merchantId: string };
  meta: { correlationId: string | undefined };
}

export class SessionAuthError extends Error {
  readonly status: number;
  readonly reason: string;
  constructor(reason: string, message: string, status: number) {
    super(message);
    this.reason = reason;
    this.status = status;
  }
}

export async function mintSessionAuth(): Promise<{
  merchantId: string;
  expiresAt: number;
}> {
  let sessionToken: string;
  try {
    sessionToken = await getShopifySessionToken();
  } catch (err) {
    throw new SessionAuthError(
      "app_bridge_unavailable",
      err instanceof Error ? err.message : "App Bridge unavailable",
      0,
    );
  }

  const res = await fetch("/auth/session", {
    method: "POST",
    headers: {
      authorization: `Bearer ${sessionToken}`,
      "content-type": "application/json",
    },
  });

  const text = await res.text();
  if (!res.ok) {
    let message = res.statusText || "Session exchange failed";
    try {
      const parsed = JSON.parse(text) as { message?: string; error?: { message?: string } };
      if (parsed.error?.message) message = parsed.error.message;
      else if (parsed.message) message = parsed.message;
    } catch {
      // keep statusText
    }
    throw new SessionAuthError(
      res.status === 404 ? "merchant_not_installed" : "session_rejected",
      message,
      res.status,
    );
  }

  const parsed = JSON.parse(text) as SessionResponse;
  const expiresAt = Date.now() + parsed.data.expiresIn * 1000;
  setToken({
    token: parsed.data.token,
    merchantId: parsed.data.merchantId,
    expiresAt,
  });
  return { merchantId: parsed.data.merchantId, expiresAt };
}
